import React, { useCallback, useState, FC, useEffect } from "react";
import TagManager from "react-gtm-module";
import { useDispatch, useSelector } from "react-redux";

import { setCheckout } from "src/actions";
import {
  removeFromCheckout,
  getCheckout,
  changeCountInCheckout,
} from "src/api";
import { ApplicationStore } from "src/store";
import {
  convertImageUrl,
  formatCurrency,
  getBasketId,
  sendTagManagerGarderoboAddToCheckoutData,
} from "src/utils";

import {
  ProductNotAvailable,
  ProductNotAvailableIcon,
  Container,
  ContainerInner,
  InfoContainerInner,
  InfoContainer,
  Picture,
  Image,
  Name,
  Kind,
  Size,
  Price,
  DeleteButton,
  PriceContainer,
  OldPrice,
  ProductCount,
  CountButton,
  MinusIcon,
  PlusIcon,
  Count,
  CountTitle,
  Quantity,
} from "./CheckoutItem.styles";
import { CheckoutItemProps } from "./CheckoutItem.types";

const CheckoutItem: FC<CheckoutItemProps> = (props) => {
  const {
    item,
    isShowedCount,
    categories,
    total,
    setProductAvaliableCount,
  } = props;
  const {
    product,
    discount_price,
    id,
    size,
    gallery,
    price,
    quantity,
    in_stock_quantity,
    in_stock,
    url,
    offer_id,
  } = item;

  const imageSrc = gallery && gallery.length ? gallery[0] : "";
  const isCheckoutPage = window.location.href.includes("checkout");

  const dispatch = useDispatch();
  const { userData } = useSelector((state: ApplicationStore) => state.user);
  const { info } = useSelector((state: ApplicationStore) => state.user);
  const [isLoading, setLoading] = useState(false);
  const [count, setCount] = useState(quantity);
  const addItemTagManager = useCallback(() => {
    if (userData?.is_retail) return;
    TagManager.dataLayer({
      dataLayer: {
        event: "add-to-cart",
        vendorcode: `${product.id_1c}#${
          product.size_id_1c ? product.size_id_1c : "ONE_SIZE"
        }`,
        ecommerce: {
          currencyCode: "RUB",
          add: {
            products: [
              {
                name: product.name,
                id: product.id,
                offer: offer_id,
                price: Number(discount_price || price),
                brand: product.brand?.name,
                category: categories,
                quantity: 1,
              },
            ],
          },
        },
      },
    });
  }, [
    categories,
    price,
    product.brand?.name,
    product.id,
    product.name,
    offer_id,
  ]);
  const removeItemTagManager = useCallback(() => {
    if (userData?.is_retail) return;
    TagManager.dataLayer({
      dataLayer: {
        event: "remove-from-cart",
        vendorcode: `${product.id_1c}#${
          product.size_id_1c ? product.size_id_1c : "ONE_SIZE"
        }`,
        ecommerce: {
          remove: {
            products: [
              {
                name: product.name,
                id: product.id,
                price: Number(discount_price || price),
                brand: product.brand?.name,
                category: categories,
                quantity: 1,
              },
            ],
          },
        },
      },
    });

    TagManager.dataLayer({
      dataLayer: {
        ecommerce: null,
      },
    });

    if (total && total <= 1) {
      TagManager.dataLayer({
        dataLayer: {
          event: "clear-cart",
        },
      });
    }
  }, [categories, price, product.brand?.name, product.id, product.name, total]);

  const removeItem = useCallback(() => {
    const checkoutId = getBasketId() || info?.checkoutId;
    if (checkoutId) {
      setLoading(true);

      removeFromCheckout(checkoutId, id)
        .then(() => {
          getCheckout(checkoutId, true).then(({ data }) => {
            dispatch(setCheckout(data));
            const { items } = data.basket;
            sendTagManagerGarderoboAddToCheckoutData({
              offerid: JSON.stringify(items.map((item) => item.offer_id)),
            });
            TagManager.dataLayer({
              dataLayer: {
                removefromcart: product.id,
              },
            });
            removeItemTagManager();
          });
        })
        .catch((error) => {
          if (error.response.status === 400) {
            getCheckout(checkoutId, true).then(({ data }) => {
              dispatch(setCheckout(data));
            });
          }
        })
        .finally(() => setLoading(false));
    }
  }, [dispatch, id, info, product.id, removeItemTagManager]);

  const onChangeCount = useCallback(
    (side: string) => {
      const checkoutId = getBasketId() || info?.checkoutId;
      if (checkoutId) {
        let newCount = 0;
        if (side === "plus") {
          setCount(count + 1);
          newCount = count + 1;
        }

        if (side === "minus") {
          setCount(count - 1);
          newCount = count - 1;
        }
        setLoading(true);

        changeCountInCheckout(checkoutId, id, newCount)
          .then(() => {
            if (side === "plus") {
              addItemTagManager();
            }

            if (side === "minus") {
              removeItemTagManager();
            }
          })
          .finally(() => setLoading(false));
      }
    },
    [
      addItemTagManager,
      dispatch,
      id,
      info,
      quantity,
      removeItemTagManager,
      count,
    ]
  );

  useEffect(() => {
    if (setProductAvaliableCount)
      setProductAvaliableCount(count <= in_stock_quantity);
  }, [count, in_stock_quantity, setProductAvaliableCount]);

  if (!product) {
    return null;
  }

  return (
    <Container className={props.className}>
      <ContainerInner>
        <InfoContainer>
          {!in_stock && (
            <ProductNotAvailable>
              <ProductNotAvailableIcon>!</ProductNotAvailableIcon>
              <p>Нет в наличии</p>
            </ProductNotAvailable>
          )}
          {count > in_stock_quantity && in_stock_quantity > 0 && (
            <ProductNotAvailable>
              <ProductNotAvailableIcon>!</ProductNotAvailableIcon>
              <p>
                Превышено количество доступного товара - {in_stock_quantity}
              </p>
            </ProductNotAvailable>
          )}
          <InfoContainerInner disabled={!in_stock || count > in_stock_quantity}>
            {!window.location.href.includes("checkout") ? (
              <Name>
                <a href={url}>{product.brand?.name}</a>
              </Name>
            ) : (
              <Name>{product.brand?.name}</Name>
            )}
            <Kind>{product.type.name}</Kind>
            <Size>
              {(!isCheckoutPage || size?.name || isShowedCount) && (
                <div>
                  {size?.name ? (
                    <>Размер: {size?.name}</>
                  ) : (
                    <CountTitle>
                      Количество: {!isShowedCount && "OneSize"}
                    </CountTitle>
                  )}
                </div>
              )}
              {isShowedCount && (
                <ProductCount>
                  <CountButton
                    onClick={() => onChangeCount("minus")}
                    aria-label="Уменьшить"
                    disabled={count === 1}
                  >
                    <MinusIcon />
                  </CountButton>
                  <Count>{count}</Count>
                  <CountButton
                    onClick={() => onChangeCount("plus")}
                    aria-label="Увеличить"
                    disabled={count === in_stock_quantity}
                  >
                    <PlusIcon />
                  </CountButton>
                </ProductCount>
              )}
            </Size>
            <PriceContainer>
              {+price > +discount_price && (
                <OldPrice>{formatCurrency(Number(price || 0))}</OldPrice>
              )}

              <Price>{formatCurrency(Number(discount_price || 0))}</Price>
            </PriceContainer>
          </InfoContainerInner>
          {in_stock && (
            <DeleteButton onClick={removeItem} disabled={isLoading}>
              Удалить
            </DeleteButton>
          )}
        </InfoContainer>
        <Picture disabled={!in_stock || count > in_stock_quantity}>
          {!window.location.href.includes("checkout") ? (
            <a href={url}>
              <Image
                src={convertImageUrl(imageSrc, 310, 400)}
                alt={product.name}
              />
            </a>
          ) : (
            <Image
              src={convertImageUrl(imageSrc, 310, 400)}
              alt={product.name}
            />
          )}
          {!isShowedCount && quantity > 1 && (
            <Quantity>{quantity} шт.</Quantity>
          )}
        </Picture>
      </ContainerInner>
      {!in_stock && (
        <DeleteButton onClick={removeItem} disabled={isLoading}>
          Удалить
        </DeleteButton>
      )}
    </Container>
  );
};

export default CheckoutItem;
